import React, { useState } from "react";
import {
  FileText,
  CalendarClock,
  Receipt,
  CreditCard,
  BarChart3,
  CalendarDays,
  LineChart,
  XCircle,
  ClipboardList,
  Wallet,
  RefreshCw,
  Printer,
  Download,
  Search,
  User,
  ChevronDown,
  DatabaseZap,
  Table2,
  PieChart,
} from "lucide-react";

const sidebarItems = [
  { label: "Contract List", icon: FileText },
  { label: "Contract Expiry", icon: CalendarClock },
  { label: "Tax Payable", icon: Receipt },
  { label: "Payment Details", icon: CreditCard },
  { label: "Income Analysis", icon: BarChart3 },
  { label: "Monthwise Income", icon: CalendarDays },
  { label: "Daily Income Analysis", icon: LineChart },
  { label: "Cancelled Contracts", icon: XCircle },
  { label: "Receipt Analysis", icon: ClipboardList },
  { label: "Payment Analysis", icon: Wallet },
  { label: "Renewal List", icon: RefreshCw },
];

const topTabs = [
//   "Contract Analysis",
//   "Monthwise Income Analysis",
//   "Income Register",
];

const statusFilters = ["All", "Active", "Expiring", "Expired"];

function Field({ label, children }) {
  return (
    <div className="flex-1 min-w-[150px]">
      <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

export default function ContractAnalysis() {
  const [activeTab, setActiveTab] = useState("Contract Analysis");
  const [activeItem, setActiveItem] = useState("Contract List");
  const [status, setStatus] = useState("All");
  const [view, setView] = useState("Data View");
  const [groupView, setGroupView] = useState(false);
  const [loading, setLoading] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 font-sans">
      {/* Header */}
      <header className=" px-6 pt-5">
        <div className="flex items-center gap-3 ">
          {/* <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center shadow-sm shadow-blue-200">
            <BarChart3 className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div> */}
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">
            Contract Analysis
          </h1>
        </div>

        <nav className="flex gap-1">
          {topTabs.map((tab) => {
            const isActive = tab === activeTab;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`relative px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
                  isActive
                    ? "text-blue-600"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {tab}
                {isActive && (
                  <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-blue-600 rounded-full" />
                )}
              </button>
            );
          })}
        </nav>
      </header>

      {/* Body */}
      <div className="flex gap-4 p-4 lg:p-6">
        {/* Sidebar */}
        <aside className="w-64 shrink-0 space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-2">
            <p className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              Reports
            </p>
            <ul className="space-y-1">
              {sidebarItems.map(({ label, icon: Icon }) => {
                const isActive = label === activeItem;
                return (
                  <li key={label}>
                    <button
                      onClick={() => {
                        if (label === activeItem) return;
                        setActiveItem(label);
                        setLoading(true);
                        setTimeout(() => setLoading(false), 800);
                      }}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium ${
                        isActive
                          ? "bg-blue-50 text-blue-700"
                          : "text-slate-600"
                      }`}
                    >
                      <Icon
                        className={`w-4 h-4 shrink-0 ${
                          isActive ? "text-blue-600" : "text-slate-400"
                        }`}
                      />
                      <span className="text-left">{label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-3">
              Status
            </p>
            <div className="space-y-2">
              {statusFilters.map((s) => (
                <label
                  key={s}
                  className="flex items-center gap-2.5 cursor-pointer "
                >
                  <span
                    className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
                      status === s
                        ? "border-blue-600"
                        : "border-slate-300 "
                    }`}
                  >
                    {status === s && (
                      <span className="w-2 h-2 rounded-full bg-blue-600" />
                    )}
                  </span>
                  <input
                    type="radio"
                    name="status"
                    className="hidden"
                    checked={status === s}
                    onChange={() => setStatus(s)}
                  />
                  <span
                    className={`text-sm ${
                      status === s
                        ? "text-slate-900 font-medium"
                        : "text-slate-500"
                    }`}
                  >
                    {s}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </aside>

        {/* Main panel */}
        <main className="flex-1 min-w-0 space-y-4">
          {/* Filter bar */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <div className="flex flex-wrap items-end gap-3">
              <Field label="From">
                <input
                  type="text"
                  defaultValue="02/07/2026"
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
                />
              </Field>
              <Field label="To">
                <input
                  type="text"
                  defaultValue="02/07/2026"
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
                />
              </Field>
              <Field label="Building">
                <div className="relative">
                  <select className="w-full appearance-none rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 pr-8 text-sm text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500">
                    <option>Select building</option>
                  </select>
                  <ChevronDown className="w-4 h-4 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </Field>
              <Field label="Contract No">
                <input
                  type="text"
                  placeholder="Enter contract no"
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
                />
              </Field>
              <Field label="Contract Type">
                <div className="relative">
                  <select className="w-full appearance-none rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 pr-8 text-sm text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500">
                    <option>All types</option>
                  </select>
                  <ChevronDown className="w-4 h-4 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </Field>
              <Field label="Tenant">
                <div className="relative">
                  <User className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search tenant"
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 pl-9 pr-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
                  />
                </div>
              </Field>

              <div className="flex gap-2 ml-auto">
                <button className="inline-flex items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 shadow-sm shadow-blue-200 transition-colors">
                  <Printer className="w-4 h-4" />
                  Print
                </button>
                <button className="inline-flex items-center gap-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 text-sm font-medium px-4 py-2 transition-colors">
                  <Download className="w-4 h-4" />
                  Export
                </button>
              </div>
            </div>

            <label className="mt-3 flex items-center gap-2 w-fit cursor-pointer">
              <input
                type="checkbox"
                checked={groupView}
                onChange={() => setGroupView(!groupView)}
                className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500/40"
              />
              <span className="text-sm text-slate-600">Group view</span>
            </label>
          </div>

          {/* Data card */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm ">
            <div className="flex items-center justify-between border-b border-slate-200 px-4">
              <div className="flex gap-1">
                {["Data View", "Graphical View"].map((v) => {
                  const Icon = v === "Data View" ? Table2 : PieChart;
                  const isActive = v === view;
                  return (
                    <button
                      key={v}
                      onClick={() => setView(v)}
                      className={`relative flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                        isActive
                          ? "text-blue-600"
                          : "text-slate-500 hover:text-slate-700"
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {v}
                      {isActive && (
                        <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-blue-600 rounded-full" />
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="relative hidden sm:block">
                <Search className="w-4 h-4 text-slate-300 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input
                  disabled
                  placeholder="Search results"
                  className="w-48 rounded-lg border border-slate-200 bg-slate-50 pl-8 pr-3 py-1.5 text-sm text-slate-400 placeholder:text-slate-300"
                />
              </div>
            </div>

            {/* Empty state / Loader */}
            {loading ? (
              <div className="flex flex-col items-center justify-center py-24 px-6 text-center">
                <div className="w-10 h-10 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin mb-4" />
                <h3 className="text-sm font-semibold text-slate-700">Loading {activeItem}...</h3>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-24 px-6 text-center">
                <div className="w-16 h-16 rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-center mb-4">
                  <DatabaseZap className="w-7 h-7 text-slate-300" />
                </div>
                <h3 className="text-sm font-semibold text-slate-700">
                  No contracts match this filter
                </h3>
                <p className="text-sm text-slate-400 mt-1 max-w-sm">
                  Adjust the date range, building, or tenant filters above to
                  find the contracts you're looking for.
                </p>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}